/**
 * Proactive AI-credential refresh scheduler. Holds a single timer that fires
 * `PROACTIVE_AI_REFRESH_MARGIN_MS` before the token expires and re-arms against
 * the new expiry via the caller-supplied `refresh`. Pure logic — the caller
 * threads the `host.refresh_credential` side-effect. Resilience model
 * (transient bounded-retry, terminal stop, stale-guard, non-amplification on a
 * shared seat) is documented in `runner/CLAUDE.md` § AI Credential Mediation.
 *
 * @docLink packages/runner/dev-guide#ai-credential-mediation
 */

import { MINT_RETRY_MAX_ATTEMPTS, mintRetryDelayMs } from "./ai-credential-retry.js";

/**
 * Safety margin between the scheduled refresh and the token's declared
 * expiry. Default 5 minutes: long enough that any in-flight prompt at
 * fire time still has a usable token, short enough that a server clock
 * skew of a few seconds does not push the fire-time past expiry.
 */
export const PROACTIVE_AI_REFRESH_MARGIN_MS = 5 * 60 * 1000;

/**
 * Stale-schedule guard window. After the bounded transient retries are
 * exhausted the scheduler arms a single long timer this far out so a blip
 * during a proactive fire cannot leave refresh idle indefinitely — the
 * worst case becomes one degraded window, after which a fresh refresh is
 * attempted. Also the threshold {@link AiCredentialRefreshScheduler.ensureArmed}
 * uses to decide a previously-armed scheduler has gone stale.
 */
export const STALE_REARM_MS = 9 * 60 * 60 * 1000;

/**
 * Node/Bun `setTimeout` ceiling. A delay above this is **silently collapsed to
 * 1 ms** (the only trace is a `TimeoutOverflowWarning` on stderr), which turns
 * a far-future expiry into a re-mint hot loop rather than one fire near expiry.
 */
export const MAX_TIMER_DELAY_MS = 2 ** 31 - 1;

/**
 * Longest delay this scheduler ever hands to `setTimeout`. Kept a margin below
 * {@link MAX_TIMER_DELAY_MS} rather than at the boundary so no rounding or
 * clock skew can reach the ceiling; anything longer is served as a chain of
 * re-arms that fires nothing until the real delay has elapsed.
 */
export const MAX_REARM_MS = 24 * 24 * 60 * 60 * 1000;

const MAX_REARM_DAYS = Math.round(MAX_REARM_MS / 86_400_000);

/**
 * Outcome of a proactive refresh attempt, returned by the caller-supplied
 * {@link AiCredentialRefreshDeps.refresh}:
 *
 * - `ok` — a fresh credential was minted; `expiresAt` is its ISO expiry, or
 *   `null` for a static token (no further refresh needed).
 * - `transient` — a mediator/transport blip; the scheduler re-arms a bounded
 *   retry and ultimately a stale-schedule guard.
 * - `terminal` — the credential/account is genuinely unusable; the scheduler
 *   stops re-arming and leaves the reactive 401 path as the safety net.
 */
export type AiRefreshOutcome =
  | { status: "ok"; expiresAt: string | null }
  | { status: "transient" }
  | { status: "terminal" };

/**
 * Caller-supplied side effects the scheduler invokes.
 *
 * - `refresh()` runs the actual mint round-trip and classifies the result
 *   into an {@link AiRefreshOutcome}.
 * - `log()` receives operator-facing single-line strings (info level).
 * - `logError()` receives failure strings that MUST surface at error level
 *   (visible in `skaile session logs` and the debug panel). Defaults to
 *   `log` when omitted.
 * - `now()` is dependency-injected for deterministic timer tests; defaults
 *   to `Date.now`.
 * - `setTimeoutImpl` / `clearTimeoutImpl` are injected for fake-timer
 *   tests; default to the host's `setTimeout` / `clearTimeout`.
 */
export interface AiCredentialRefreshDeps {
  refresh: () => Promise<AiRefreshOutcome>;
  log: (line: string) => void;
  logError?: (line: string) => void;
  now?: () => number;
  setTimeoutImpl?: (handler: () => void, ms: number) => { unref?: () => void };
  clearTimeoutImpl?: (handle: { unref?: () => void }) => void;
  marginMs?: number;
}

export interface AiCredentialRefreshScheduler {
  /**
   * Arm the timer for `expiresAtIso - marginMs`. Cancels any prior pending
   * fire. `null` / `undefined` cancels without re-arming (static PAT
   * semantics) and marks the credential static. If the fire-time has
   * already passed, runs `refresh()` on the next tick. Records the
   * credential as healthy (resets the transient-retry counter and the
   * stale clock).
   */
  schedule(expiresAtIso: string | null | undefined): void;
  /**
   * Re-arm hook for the configure/wake path. Kicks a fresh refresh on the next
   * tick when the scheduler is idle (prior terminal failure), stale (no
   * successful fire in {@link STALE_REARM_MS}), or currently parked on the long
   * stale-schedule guard — so a container waking during a degraded window
   * recovers eagerly instead of waiting out the guard. No-op when a healthy
   * refresh timer is already pending or the credential is a static PAT.
   */
  ensureArmed(): void;
  /** Cancel any pending fire. Idempotent. */
  cancel(): void;
  /**
   * Whether a timer is currently armed. Exposed for tests; production
   * callers should not need this.
   */
  isPending(): boolean;
}

/**
 * Build a scheduler bound to the supplied dependencies. One scheduler
 * per session — the runner constructs it once at session_init and
 * disposes via `cancel()` during cleanup.
 */
export function createAiCredentialRefreshScheduler(
  deps: AiCredentialRefreshDeps,
): AiCredentialRefreshScheduler {
  const now = deps.now ?? (() => Date.now());
  const setTimeoutImpl =
    deps.setTimeoutImpl ??
    ((handler: () => void, ms: number) => {
      const handle = setTimeout(handler, ms);
      // Node + Bun timers expose `unref` so this scheduler does not pin
      // the event loop alive.
      return handle as unknown as { unref?: () => void };
    });
  const clearTimeoutImpl =
    deps.clearTimeoutImpl ??
    ((handle: { unref?: () => void }) => {
      clearTimeout(handle as unknown as ReturnType<typeof setTimeout>);
    });
  const marginMs = deps.marginMs ?? PROACTIVE_AI_REFRESH_MARGIN_MS;
  const logError = deps.logError ?? deps.log;

  let timer: { unref?: () => void } | null = null;
  // Consecutive transient retries already armed since the last success.
  let transientRetryCount = 0;
  // `now()` of the last known-good credential, or null when none confirmed.
  let lastSuccessAt: number | null = null;
  // True when the active credential is a static PAT (no expiry to refresh).
  let isStatic = false;
  // True while the long stale-schedule guard is the pending timer — so
  // `ensureArmed()` can revive eagerly on wake instead of waiting it out.
  let inStaleGuard = false;

  function cancel(): void {
    if (timer !== null) {
      clearTimeoutImpl(timer);
      timer = null;
    }
  }

  function armTimer(handler: () => void, ms: number): void {
    cancel();
    arm(handler, now() + ms);
  }

  /**
   * Arm one leg of a (possibly chained) wait toward `deadlineMs`. A wait
   * longer than {@link MAX_REARM_MS} is served as successive legs that only
   * re-arm — `handler` runs when the deadline is reached, never early. Each
   * leg re-derives the remaining time from the clock, so per-leg timer
   * lateness cannot accumulate across a chain of a dozen-plus legs.
   */
  function arm(handler: () => void, deadlineMs: number): void {
    const remainingMs = deadlineMs - now();
    if (remainingMs > MAX_REARM_MS) {
      deps.log(
        `[ai-credential-refresh] delay ${Math.round(remainingMs / 1000)}s exceeds the ${MAX_REARM_DAYS}d re-arm cap; re-arming in ${MAX_REARM_DAYS}d (no refresh fired)`,
      );
      timer = setTimeoutImpl(() => arm(handler, deadlineMs), MAX_REARM_MS);
    } else {
      timer = setTimeoutImpl(handler, remainingMs);
    }
    timer.unref?.();
  }

  function isStale(): boolean {
    return lastSuccessAt !== null && now() - lastSuccessAt > STALE_REARM_MS;
  }

  async function fire(): Promise<void> {
    timer = null;
    let outcome: AiRefreshOutcome;
    try {
      outcome = await deps.refresh();
    } catch (err) {
      // A throw is a transport/network blip — treat as transient, not fatal.
      logError(
        `[ai-credential-refresh] refresh threw: ${err instanceof Error ? err.message : String(err)}; treating as transient`,
      );
      outcome = { status: "transient" };
    }

    if (outcome.status === "ok") {
      schedule(outcome.expiresAt);
      return;
    }

    if (outcome.status === "terminal") {
      transientRetryCount = 0;
      inStaleGuard = false;
      logError(
        "[ai-credential-refresh] proactive refresh hit a terminal failure; not re-arming (reactive 401 path remains the safety net)",
      );
      return;
    }

    // Transient: re-arm a bounded retry, then a long stale-schedule guard.
    if (transientRetryCount < MINT_RETRY_MAX_ATTEMPTS - 1) {
      transientRetryCount += 1;
      inStaleGuard = false;
      const delayMs = mintRetryDelayMs(transientRetryCount);
      logError(
        `[ai-credential-refresh] proactive refresh transient failure; bounded retry ${transientRetryCount}/${MINT_RETRY_MAX_ATTEMPTS - 1} in ${Math.round(delayMs / 1000)}s`,
      );
      armTimer(() => void fire(), delayMs);
      return;
    }

    transientRetryCount = 0;
    inStaleGuard = true;
    logError(
      `[ai-credential-refresh] proactive refresh failed after ${MINT_RETRY_MAX_ATTEMPTS} attempts; reactive 401 path is the fallback, re-checking in ${Math.round(STALE_REARM_MS / 3_600_000)}h`,
    );
    armTimer(() => void fire(), STALE_REARM_MS);
  }

  function schedule(expiresAtIso: string | null | undefined): void {
    cancel();
    inStaleGuard = false;
    if (!expiresAtIso) {
      // Static token (PAT) or unknown expiry — nothing to schedule. A null
      // expiry still represents a working credential, so reset health state.
      isStatic = true;
      transientRetryCount = 0;
      lastSuccessAt = now();
      return;
    }
    const expiresAtMs = Date.parse(expiresAtIso);
    if (!Number.isFinite(expiresAtMs)) return;
    // A parseable future expiry is a known-good credential.
    isStatic = false;
    transientRetryCount = 0;
    lastSuccessAt = now();
    const delayMs = expiresAtMs - now() - marginMs;
    if (delayMs <= 0) {
      deps.log(
        `[ai-credential-refresh] token within ${marginMs / 1000}s of expiry; refreshing now`,
      );
      armTimer(() => void fire(), 0);
    } else {
      deps.log(
        `[ai-credential-refresh] scheduled in ${Math.round(delayMs / 1000)}s (expiresAt=${expiresAtIso})`,
      );
      armTimer(() => void fire(), delayMs);
    }
  }

  function ensureArmed(): void {
    // Static PAT: nothing to refresh.
    if (isStatic) return;
    // Healthy refresh timer pending (not the long stale guard, not stale):
    // leave it alone.
    if (timer !== null && !inStaleGuard && !isStale()) return;
    // Idle (terminal failure), stale, or parked on the stale guard: kick a
    // fresh refresh on the next tick so a waking container recovers eagerly.
    deps.log("[ai-credential-refresh] ensureArmed: reviving idle/stale schedule");
    transientRetryCount = 0;
    inStaleGuard = false;
    armTimer(() => void fire(), 0);
  }

  return {
    schedule,
    ensureArmed,
    cancel,
    isPending: () => timer !== null,
  };
}
